import { ErrorAlert } from "#components/ErrorAlert"
import { ColumnMappingDialog } from "#components/expense-import/ColumnMappingDialog"
import { ImportProgress } from "#components/expense-import/ImportProgress"
import ExpenseFiltersComponent from "#components/expenses/expense-filters"
import { ExpenseDialogs } from "#components/expenses/ExpenseDialogs"
import { ExpenseTable } from "#components/expenses/ExpenseTable"
import { createExpenseTableColumns } from "#components/expenses/ExpenseTableColumns"
import { Button } from "#components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "#components/ui/card"
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "#components/ui/drawer"
import { Popover, PopoverContent, PopoverTrigger } from "#components/ui/popover"
import { Body } from "#components/ui/typography"
import { useAccount } from "#contexts/AccountContext"
import { useBulkExpenseImport } from "#hooks/use-bulk-expense-import"
import { useCanPerformMutations } from "#hooks/use-can-perform-mutations"
import { useCategories } from "#hooks/use-categories"
import { useCSVImport } from "#hooks/use-csv-import"
import { useCurrencyFormatters } from "#hooks/use-currency-formatters"
import { useDebounce } from "#hooks/use-debounce"
import { useDesktop } from "#hooks/use-desktop"
import {
  useCreateExpense,
  useDeleteExpense,
  useExpenses,
  useUpdateExpense,
} from "#hooks/use-expenses"
import { useI18n } from "#hooks/use-i18n"
import { usePartners } from "#hooks/use-partners"
import { useUrlFilters } from "#hooks/use-url-filters"
import { slideUp } from "#lib/animations"
import { getUserFriendlyErrorMessage } from "#lib/errors"
import { deserializeExpenseFilters, serializeExpenseFilters } from "#lib/filter-serialization"
import { queryKeys } from "#lib/query-keys"
import type {
  CreateExpense,
  Expense,
  ExpenseFilters,
  UpdateExpense,
} from "@home-finance-manager/shared/types/expenses"
import { useQueryClient } from "@tanstack/react-query"
import {
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
  type SortingState,
} from "@tanstack/react-table"
import { motion } from "framer-motion"
import { Filter } from "lucide-react"
import React, { useCallback, useMemo, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { ExpensesHeaderActions } from "../components/expenses/ExpensesHeaderActions"
import { PageHeader } from "../components/page-header"

const Expenses: React.FC = () => {
  const { t } = useI18n()
  const { formatCurrency } = useCurrencyFormatters()
  const canPerformMutations = useCanPerformMutations()
  const [showDialog, setShowDialog] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [searchParams, setSearchParams] = useSearchParams()

  // Get sorting from URL
  const sorting = useMemo<SortingState>(() => {
    const sortParam = searchParams.get("sort")
    if (!sortParam) return []
    try {
      const parsed = JSON.parse(sortParam)
      return Array.isArray(parsed) ? parsed : []
    } catch {
      return []
    }
  }, [searchParams])

  const setSorting = useCallback(
    (newSorting: SortingState | ((prev: SortingState) => SortingState)) => {
      setSearchParams((prev) => {
        const updatedSorting = typeof newSorting === "function" ? newSorting(sorting) : newSorting
        const newParams = new URLSearchParams(prev)
        if (updatedSorting.length === 0) {
          newParams.delete("sort")
        } else {
          newParams.set("sort", JSON.stringify(updatedSorting))
        }
        return newParams
      })
    },
    [setSearchParams, sorting]
  )
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null)
  const [deletingExpense, setDeletingExpense] = useState<Expense | null>(null)
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [showImportDialog, setShowImportDialog] = useState(false)
  const [showProgressDialog, setShowProgressDialog] = useState(false)
  const [csvFile, setCsvFile] = useState<File | null>(null)
  const [restoreToStep, setRestoreToStep] = useState<1 | 2 | 3 | undefined>(undefined)
  const isDesktop = useDesktop()
  const { currentAccount } = useAccount()
  const csvImport = useCSVImport()
  const bulkImport = useBulkExpenseImport(currentAccount?.id || 0)

  // Use URL params for filters
  const [urlFilters, setUrlFilters] = useUrlFilters<ExpenseFilters>(
    {},
    serializeExpenseFilters,
    deserializeExpenseFilters,
    ["search", "categoryIds", "recurringTypes", "isVirtual", "includeSplits"]
  )

  // Local search state for debouncing (will sync to URL)
  const [localSearch, setLocalSearch] = useState<string>(urlFilters.search || "")

  // Sync local search when URL filters change (but not from our own updates)
  const previousUrlSearchRef = React.useRef<string | undefined>(urlFilters.search)
  React.useEffect(() => {
    if (previousUrlSearchRef.current !== urlFilters.search) {
      previousUrlSearchRef.current = urlFilters.search
      setLocalSearch(urlFilters.search || "")
    }
  }, [urlFilters.search])

  // Debounce search input to avoid too many URL updates
  const debouncedSearch = useDebounce(localSearch, 300)

  // Update URL when debounced search changes
  const previousDebouncedSearchRef = React.useRef<string | undefined>(debouncedSearch)
  React.useEffect(() => {
    if (previousDebouncedSearchRef.current === debouncedSearch) {
      return
    }
    previousDebouncedSearchRef.current = debouncedSearch
    const trimmedSearch = debouncedSearch?.trim() || undefined
    setUrlFilters((prev) => {
      if (trimmedSearch === prev.search) {
        return prev
      }
      return {
        ...prev,
        search: trimmedSearch,
      }
    })
  }, [debouncedSearch, setUrlFilters])

  const queryClient = useQueryClient()

  // Memoize filters to prevent unnecessary re-renders and query refetches
  const memoizedFilters = React.useMemo(() => {
    const result: ExpenseFilters = {}

    // Only include search if it's not empty
    const trimmedSearch = urlFilters.search?.trim()
    if (trimmedSearch && trimmedSearch.length > 0) {
      result.search = trimmedSearch
    }

    // Only include other filters if they're set
    if (urlFilters.categoryIds && urlFilters.categoryIds.length > 0) {
      result.categoryIds = urlFilters.categoryIds
    }
    if (urlFilters.recurringTypes && urlFilters.recurringTypes.length > 0) {
      result.recurringTypes = urlFilters.recurringTypes
    }
    if (urlFilters.isVirtual !== undefined) {
      result.isVirtual = urlFilters.isVirtual
    }

    return result
  }, [urlFilters.search, urlFilters.categoryIds, urlFilters.recurringTypes, urlFilters.isVirtual])

  // Reset all filters
  const handleFiltersChange = useCallback(
    (newFilters: ExpenseFilters) => {
      setUrlFilters(newFilters)
      // Sync local search state with new filters
      setLocalSearch(newFilters.search || "")
      // If filters are being reset (empty), invalidate queries to force refetch
      if (!newFilters || Object.keys(newFilters).length === 0) {
        queryClient.invalidateQueries({ queryKey: queryKeys.expenses.all })
      }
    },
    [setUrlFilters, queryClient]
  )

  // Check if any filters are active
  const hasActiveFilters = React.useMemo(() => {
    return (
      (urlFilters.search && urlFilters.search.trim().length > 0) ||
      (urlFilters.categoryIds && urlFilters.categoryIds.length > 0) ||
      (urlFilters.recurringTypes && urlFilters.recurringTypes.length > 0) ||
      urlFilters.isVirtual !== undefined
    )
  }, [urlFilters.search, urlFilters.categoryIds, urlFilters.recurringTypes, urlFilters.isVirtual])

  // Use stable filters object - always pass an object (even if empty) to prevent query key changes
  // This ensures React Query doesn't see undefined vs object as different query keys
  const stableFilters = React.useMemo(() => {
    return memoizedFilters
  }, [memoizedFilters])

  const {
    data: expensesData,
    isLoading: expensesLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useExpenses(stableFilters)
  const expenses = React.useMemo(() => {
    return expensesData?.pages.flatMap((page) => page.data) ?? []
  }, [expensesData])
  const { data: categories = [] } = useCategories()
  const { data: partners = [] } = usePartners()
  const createExpense = useCreateExpense()
  const updateExpense = useUpdateExpense()
  const deleteExpense = useDeleteExpense()

  const handleEdit = useCallback((expense: Expense) => {
    setEditingExpense(expense)
    setShowDialog(true)
  }, [])

  const columns = useMemo(
    () =>
      createExpenseTableColumns({
        categories,
        onEdit: handleEdit,
        onDelete: setDeletingExpense,
        isOwner: canPerformMutations,
        t,
        formatCurrency,
      }),
    [categories, handleEdit, canPerformMutations, t, formatCurrency]
  )

  const table = useReactTable({
    data: expenses,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: {
      sorting,
    },
  })

  const handleCloseDialog = useCallback(() => {
    setShowDialog(false)
    setEditingExpense(null)
  }, [])

  const handleCreateExpense = useCallback(
    async (data: CreateExpense) => {
      try {
        setError(null)
        await createExpense.mutateAsync(data)
        setShowDialog(false)
      } catch (err) {
        setError(getUserFriendlyErrorMessage(err))
      }
    },
    [createExpense]
  )

  const handleUpdateExpense = useCallback(
    async (id: number, data: UpdateExpense) => {
      try {
        setError(null)
        await updateExpense.mutateAsync({ id, data })
        setShowDialog(false)
      } catch (err) {
        setError(getUserFriendlyErrorMessage(err))
      }
    },
    [updateExpense]
  )

  const handleDeleteExpense = useCallback(
    async (id: number) => {
      try {
        setError(null)
        await deleteExpense.mutateAsync(id)
        setDeletingExpense(null)
      } catch (err) {
        setError(getUserFriendlyErrorMessage(err))
        setDeletingExpense(null)
      }
    },
    [deleteExpense]
  )

  if (expensesLoading) {
    return (
      <div className="min-h-screen text-primary p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <Body className="text-secondary-foreground">{t("pages.expenses.loading")}</Body>
        </div>
      </div>
    )
  }

  return (
    <div className="p-4 sm:p-6 flex flex-col">
      <ErrorAlert error={error} onDismiss={() => setError(null)} />
      <PageHeader
        title={t("pages.expenses.pageTitle")}
        subtitle={t("pages.expenses.pageSubtitle")}
        actions={
          <ExpensesHeaderActions
            isOwner={canPerformMutations}
            onAddExpense={() => {
              setError(null)
              setShowDialog(true)
            }}
            onImportCSV={() => {
              const input = document.createElement("input")
              input.type = "file"
              input.accept = ".csv"
              input.onchange = async (e) => {
                const file = (e.target as HTMLInputElement).files?.[0]
                if (file) {
                  setCsvFile(file)
                  try {
                    await csvImport.parseFile(file, {
                      separator: ";",
                      dateFormat: "dd/MM/yyyy",
                      decimalSeparator: ",",
                    })
                    setShowImportDialog(true)
                  } catch (error) {
                    console.error("Failed to parse CSV:", error)
                  }
                }
              }
              input.click()
            }}
          />
        }
      />

      {/* Expenses Table with Filters */}
      <motion.div initial="hidden" animate="visible" variants={slideUp} className="flex-1 min-h-0">
        <Card className="flex-1 min-h-0 flex flex-col">
          <CardHeader className="flex flex-row items-center justify-between">
            <div className="flex flex-col gap-1.5">
              <CardTitle>{t("pages.expenses.tableTitle")}</CardTitle>
              <CardDescription>{t("pages.expenses.tableDescription")}</CardDescription>
            </div>
            {isDesktop ? (
              <Popover open={filtersOpen} onOpenChange={setFiltersOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="icon" className="relative">
                    <Filter className="w-5 h-5" />
                    {hasActiveFilters && (
                      <span className="absolute -top-1 -right-1 w-3 h-3 bg-primary rounded-full" />
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80" align="end">
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-medium leading-none mb-4">
                        {t("pages.expenses.filters")}
                      </h4>
                      <ExpenseFiltersComponent
                        filters={urlFilters}
                        onFiltersChange={handleFiltersChange}
                        onApply={() => {
                          setFiltersOpen(false)
                        }}
                        onReset={() => {
                          setFiltersOpen(false)
                        }}
                      />
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            ) : (
              <Drawer open={filtersOpen} onOpenChange={setFiltersOpen}>
                <DrawerTrigger asChild>
                  <Button variant="outline" size="icon" className="relative">
                    <Filter className="w-5 h-5" />
                    {hasActiveFilters && (
                      <span className="absolute -top-1 -right-1 w-3 h-3 bg-primary rounded-full" />
                    )}
                  </Button>
                </DrawerTrigger>
                <DrawerContent>
                  <DrawerHeader>
                    <DrawerTitle>{t("pages.expenses.filters")}</DrawerTitle>
                    <DrawerDescription>{t("pages.expenses.filtersDescription")}</DrawerDescription>
                  </DrawerHeader>
                  <div className="px-4 pb-4 overflow-y-auto">
                    <ExpenseFiltersComponent
                      filters={urlFilters}
                      onFiltersChange={handleFiltersChange}
                      onApply={() => {
                        setFiltersOpen(false)
                      }}
                      onReset={() => {
                        setFiltersOpen(false)
                      }}
                    />
                  </div>
                </DrawerContent>
              </Drawer>
            )}
          </CardHeader>
          <CardContent className="flex-1 min-h-0 flex flex-col">
            <ExpenseTable table={table} />
            {hasNextPage && (
              <div className="mt-4 flex justify-center">
                <Button
                  variant="outline"
                  onClick={() => fetchNextPage()}
                  disabled={isFetchingNextPage}
                >
                  {isFetchingNextPage
                    ? t("pages.expenses.loadingMore")
                    : t("pages.expenses.loadMore")}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Create/Edit and Delete Dialogs */}
      <ExpenseDialogs
        showDialog={showDialog}
        editingExpense={editingExpense}
        onCloseDialog={() => {
          handleCloseDialog()
          setError(null)
        }}
        categories={categories}
        partners={partners}
        onCreateExpense={handleCreateExpense}
        onUpdateExpense={handleUpdateExpense}
        isCreating={createExpense.isPending}
        isUpdating={updateExpense.isPending}
        deletingExpense={deletingExpense}
        onDeleteExpense={handleDeleteExpense}
        onCloseDeleteDialog={() => {
          setDeletingExpense(null)
          setError(null)
        }}
        isDeleting={deleteExpense.isPending}
        error={error}
        isOwner={canPerformMutations}
      />

      {/* CSV Import Dialogs */}
      {csvImport.parsedCSV && (
        <ColumnMappingDialog
          open={showImportDialog}
          onOpenChange={(open) => {
            setShowImportDialog(open)
            if (!open) {
              setCsvFile(null)
              csvImport.reset()
              setRestoreToStep(undefined)
            }
          }}
          headers={csvImport.parsedCSV.headers}
          allRows={csvImport.parsedCSV.rows}
          categories={categories}
          file={csvFile}
          onReParse={async (file, config) => {
            await csvImport.parseFile(file, config)
          }}
          onConfirm={async (mapping, config, selectedRows) => {
            setShowImportDialog(false)
            setShowProgressDialog(true)
            try {
              setError(null)
              await bulkImport.startImport(
                csvImport.parsedCSV!.rows,
                mapping,
                config,
                selectedRows,
                categories
              )
            } catch (error) {
              setError(getUserFriendlyErrorMessage(error))
              setShowProgressDialog(false)
            }
          }}
          restoreToStep={restoreToStep}
        />
      )}

      {bulkImport.progress && (
        <ImportProgress
          open={showProgressDialog}
          onOpenChange={(open) => {
            setShowProgressDialog(open)
            if (!open && csvImport.parsedCSV) {
              // Keep the CSV data available for going back
            }
          }}
          progress={bulkImport.progress}
          onPause={bulkImport.pauseImport}
          onResume={() => {
            if (bulkImport.progress) {
              bulkImport.resumeImport(bulkImport.progress.id)
            }
          }}
          onCancel={bulkImport.cancelImport}
          onGoBack={() => {
            setShowProgressDialog(false)
            // Reopen the import dialog if we have CSV data, starting at step 3 (review)
            if (csvImport.parsedCSV && csvFile) {
              setRestoreToStep(3)
              setShowImportDialog(true)
            }
          }}
        />
      )}
    </div>
  )
}

export default Expenses
